import { createSlice } from '@reduxjs/toolkit';

export const autoshipSlice = createSlice({
  name: 'autoship',
  initialState: {
    userId: '',
    id: '',
    isEditing: true,
    isPws: false,
    cityDropdown: [],
    shippingAddress: {},
    billingInfo: {
      billingAddress: {
        sameAsShipping: false,
      },
      cardInfo: {},
    },
    items: [],
    delivery: {
      isPvq: false,
      pvqInsured: false,
    },
    membershipInfo: {
      BV: 0,
      PV: 0,
      PGPV: 0,
      placedPvq: false,
      leftForNextRank: 0,
    },
    autoshipDetails: {
      name: '',
      nextShipment: '',
      frequency: '',
    },
    totals: {
      discountLevel: 0,
      bvTotal: 0,
      pvTotal: 0,
      subtotal: '',
      discounts: '',
      tax: '',
      shipping: '',
      total: '',
    },
    formsWithUnsavedChanges: [],
    loading: true,
    error: '',
    totalsCalculated: true,
    controls: {
      editingAlert: false,
      editing: {
        shipping: false,
        cardInfo: false,
        billingAddress: false,
        autoship: false,
      },
      snackbar: {
        message: '',
        type: '',
      },
      sameAsShipping: false,
      cartDataLoaded: false,
      checkoutError: false,
      hasBadCoupon: false,
    },
    // the following are used for Cart flow only
    accessToken: '',
    attributes: {},
    billAddress: {},
    cartSystemId: '',
    conditions: [],
    createdDate: {},
    createdDateString: '',
    discountPercent: 0,
    guestOrderLimit: 0,
    hasItems: false,
    itemCount: 0,
    orderNumber: null,
    orderTotals: {},
    orderTotalsType: '',
    paymentType: null,
    placedDate: null,
    placedDateString: '',
    shipping: {
      type: '',
      data: {
        shipVia: 0,
        autoShipIsMonthly: false,
        isAutoShip: false,
        shipAddress: {
          tempAddressId: 0,
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          isCommercial: false,
          geoCode: '',
          phone: '',
          email: '',
          nameLine: '',
          verified: false,
          id: 0,
          formattedState: '',
          formattedPhone: '',
          type: '',
          data: {
            tempAddressId: 0,
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            postalCode: '',
            country: '',
            isCommercial: false,
            geoCode: '',
            phone: '',
            email: '',
            nameLine: '',
            verified: false,
            id: 0,
            formattedState: '',
            formattedPhone: '',
          },
        },
      },
    },
    shipViaOptions: [],
    shippingType: '',
    site: {},
    siteType: '',
    status: '',
    subtotal: 0,
    total: 0,
    user: {},
    userType: '',
    wasPlaced: false,
    cartPaymentInfo: {},
    cartPaymentType: '',
    disableSubmitOrder: false,
    hideErrorModal: false,
    disableAutoComplete: false,
    guestOrderLimitExceeded: false,
  },
  reducers: {
    /**
     * Intended to keep track of forms with unsaved changes. Ideally,
     * for example, if the "card info" form has unsaved changes, you
     * could add an arbitrary sting like "card info" to the array. This
     * array's length is checked when a user leaves the page, and sends
     * an alert if anything is in the array.
     *
     * @example
     * const AddressComponent () => {
     *  const dispatch = useDispatch();
     *  ...
     *  useEffect(() => {
     *    if (formHasChanges) {
     *      dispatch(addUnsavedChanges("address form"));
     *      return;
     *    }
     *    dispatch(removeUnsavedChanges("address form"));
     *  }, [formHasChanges])
     */
    addUnsavedChanges: (state, action) => {
      const existing = state.formsWithUnsavedChanges;
      if (existing.includes(action.payload)) return;
      return {
        ...state,
        formsWithUnsavedChanges: [...existing, action.payload],
      };
    },

    removeUnsavedChanges: (state, action) => {
      const newChanges = state.formsWithUnsavedChanges.filter(
        (form: string) => form !== action.payload
      );

      return {
        ...state,
        formsWithUnsavedChanges: newChanges,
      };
    },

    setState: (state, action) => {
      return {
        ...state,
        ...action.payload,
        loading: false,
        totalsCalculated: true,
      };
    },

    updateItemQty: (state, action) => {
      const { id, quantity } = action.payload;
      const items = state.items;
      if (quantity === 0) {
        // if quantity == 0 filter item out from items array
        const newItems = items.filter((i) => i.itemCode !== id);
        return {
          ...state,
          items: newItems,
          loading: false,
          totalsCalculated: false,
        };
      }

      // return a map of new items. If item id matches, update the quantity and return the rest
      const newItems = items.map((i) => {
        return i.itemCode === id ? { ...i, quantity } : i;
      });
      return {
        ...state,
        items: newItems,
        loading: false,
        totalsCalculated: false,
      };
    },

    setIsPws: (state, action: { payload: boolean }) => {
      return { ...state, isPws: action.payload };
    },

    setLoading: (state, action: { payload: boolean }) => {
      return { ...state, loading: action.payload };
    },

    setError: (state, action: { payload: string }) => {
      return { ...state, error: action.payload };
    },

    setTotals: (state, action: { payload: StateData['totals'] }) => {
      return { ...state, totals: action.payload, totalsCalculated: true };
    },

    updateBillingInfo: (
      state,
      action: { payload: StateData['billingInfo'] }
    ) => {
      return { ...state, billingInfo: action.payload };
    },

    updateAutoshipDetails: (state, action) => {
      return { ...state, autoshipDetails: action.payload };
    },

    updateCardInfo: (
      state,
      action: { payload: StateData['billingInfo']['cardInfo'] }
    ) => {
      return {
        ...state,
        billingInfo: {
          ...state.billingInfo,
          cardInfo: action.payload,
        },
      };
    },

    updateBillingAddress: (
      state,
      action: { payload: StateData['billingInfo']['billingAddress'] }
    ) => {
      return {
        ...state,
        billingInfo: {
          ...state.billingInfo,
          billingAddress: action.payload,
        },
      };
    },

    updateShippingAddress: (
      state,
      action: { payload: StateData['shippingAddress'] }
    ) => {
      return { ...state, shippingAddress: action.payload };
    },

    setCityDropdown: (
      state,
      action: { payload: StateData['cityDropdown'] }
    ) => {
      return { ...state, cityDropdown: action.payload };
    },

    setDisableSubmitOrder: (state, action: { payload: boolean }) => {
      return { ...state, disableSubmitOrder: action.payload };
    },

    setSameAsShippingAddress: (state, action: { payload: boolean }) => {
      const sameAsShipping = action.payload;

      return {
        ...state,
        billingInfo: {
          ...state.billingInfo,
          billingAddress: {
            ...state.shippingAddress,
            sameAsShipping,
          },
        },
      };
    },

    setCartSameAsShipping: (state, action: { payload: any }) => {
      const sameAsShipping = action.payload;

      return {
        ...state,
        attributes: {
          ...state.attributes,
          billAddressSameAsShipAddress: sameAsShipping,
        },
        controls: {
          ...state.controls,
          sameAsShipping,
        },
        billAddress: {
          ...state.billAddress,
          addressLine1: state.shipping.data?.shipAddress.addressLine1,
          addressLine2: state.shipping.data?.shipAddress.addressLine2,
          city: state.shipping.data?.shipAddress.city,
          state: state.shipping.data?.shipAddress.state,
          postalCode: state.shipping.data?.shipAddress.postalCode,
          country: state.shipping.data?.shipAddress.country,
          isCommercial: state.shipping.data?.shipAddress.isCommercial,
          geoCode: state.shipping.data?.shipAddress.geoCode,
          phone: state.shipping.data?.shipAddress.phone,
          email: state.shipping.data?.shipAddress.email,
          nameLine: state.shipping.data?.shipAddress.nameLine,
          verified: state.shipping.data?.shipAddress.verified,
          formattedState: state.shipping.data?.shipAddress.formattedState,
          type: state.shipping.data?.shipAddress.type,
          data: state.shipping.data?.shipAddress.data,
        },
        billingInfo: {
          ...state.billingInfo,
          billingAddress: {
            ...state.shipping.data?.shipAddress,
            sameAsShipping,
          },
        },
      };
    },

    addItem: (state, action: { payload: AutoshipItem }) => {
      const { itemCode: id } = action.payload;

      const exists = state.items?.some((i) => i?.itemCode === id);
      // if the item already exists, add 1 to it's quantity so we dont have duplicates
      if (exists) {
        const newItems = state.items?.map((i) => {
          return i.itemCode === id
            ? { ...i, quantity: Number(i.quantity) + 1 }
            : i;
        });

        return {
          ...state,
          items: newItems,
          loading: false,
          totalsCalculated: false,
        };
      }

      return {
        ...state,
        items: [...state.items, action.payload],
        loading: false,
        totalsCalculated: false,
      };
    },

    setItems: (state, action: { payload: AutoshipItem[] }) => {
      return { ...state, items: action.payload };
    },

    setEditingControls: (
      state,
      action: {
        payload: {
          key: keyof StateData['controls']['editing'];
          value: boolean;
        };
      }
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        controls: {
          ...state.controls,
          editing: {
            ...state.controls.editing,
            [key]: value,
          },
        },
      };
    },
    setEditingAlert: (state, action: { payload: boolean }) => {
      return {
        ...state,
        controls: { ...state.controls, editingAlert: action.payload },
      };
    },

    setSnackbar: (
      state,
      action: {
        payload: {
          message: string;
          type?: 'success' | 'warning' | 'error' | 'info';
          delay?: number;
        };
      }
    ) => {
      const { message, type = 'success', delay = 4500 } = action.payload;
      return {
        ...state,
        controls: {
          ...state.controls,
          snackbar: {
            message,
            type,
            delay,
          },
        },
      };
    },

    // below setters are for Cart data

    setHideErrorModal: (state, action: { payload: boolean }) => {
      return {
        ...state,
        hideErrorModal: action.payload,
      };
    },
    setCheckoutError: (state, action: { payload: boolean }) => {
      return {
        ...state,
        checkoutError: action.payload,
      };
    },
    setCartPaymentInfo: (
      state,
      action: { payload: StateData['cartPaymentInfo'] }
    ) => {
      return {
        ...state,
        cartPaymentInfo: action.payload,
      };
    },
    setCartPaymentType: (state, action: { payload: string }) => {
      return {
        ...state,
        cartPaymentType: action.payload,
      };
    },
    setCartCouponCode: (state, action: { payload: string }) => {
      return {
        ...state,
        attributes: {
          ...state.attributes,
          couponCodeId: action.payload,
        },
      };
    },
    setCartIsPvq: (state, action: { payload: boolean }) => {
      return {
        ...state,
        attributes: {
          ...state.attributes,
          isPvq: action.payload,
        },
      };
    },
    setCartWarningMessage: (state, action: { payload: string }) => {
      return {
        ...state,
        attributes: {
          ...state.attributes,
          emergencyCheckoutWarningMessage: action.payload,
        },
      };
    },
    setCartDiscountPercent: (state, action: { payload: number }) => {
      return {
        ...state,
        discountPercent: action.payload,
      };
    },
    setCartTotals: (state, action: { payload: StateData['orderTotals'] }) => {
      return {
        ...state,
        orderTotals: action.payload,
      };
    },
    setCartShipViaOptions: (
      state,
      action: { payload: StateData['shipViaOptions'] }
    ) => {
      return {
        ...state,
        shipViaOptions: action.payload,
      };
    },
    setCartShipVia: (state, action: { payload: number }) => {
      return {
        ...state,
        shipping: {
          ...state.shipping,
          data: {
            ...state.shipping.data,
            shipVia: Number(action.payload),
          },
        },
      };
    },
    setCartShipGeoCode: (state, action: { payload: string }) => {
      return {
        ...state,
        shipping: {
          ...state.shipping,
          data: {
            ...state.shipping.data,
            shipAddress: {
              ...state.shipping.data.shipAddress,
              geoCode: action.payload,
            },
          },
        },
      };
    },
    setCartAutoshipEnabled: (state, action: { payload: boolean }) => {
      return {
        ...state,
        shipping: {
          ...state.shipping,
          data: {
            ...state.shipping.data,
            isAutoShip: action.payload,
          },
        },
      };
    },
    toggleCartAutoshipFrequency: (state, action: { payload: boolean }) => {
      return {
        ...state,
        shipping: {
          ...state.shipping,
          data: {
            ...state.shipping.data,
            autoShipIsMonthly: action.payload,
          },
        },
      };
    },
    setShippingAddressVerified: (state, action: { payload: boolean }) => {
      return {
        ...state,
        shipping: {
          ...state.shipping,
          data: {
            ...state.shipping.data,
            shipAddress: {
              ...state.shipping.data.shipAddress,
              verified: action.payload,
            },
          },
        },
      };
    },
    setCartShippingAddress: (
      state,
      action: { payload: Partial<StateData['shipping']['data']['shipAddress']> }
    ) => {
      return {
        ...state,
        shipping: {
          ...state.shipping,
          data: {
            ...state.shipping.data,
            shipAddress: {
              ...state.shipping.data.shipAddress,
              city: action.payload.city,
              addressLine1: action.payload.addressLine1,
              addressLine2: action.payload.addressLine2,
              state: action.payload.state,
              postalCode: action.payload.postalCode,
              country: action.payload.country,
              nameLine: action.payload.nameLine,
              phone: action.payload.phone,
            },
          },
        },
      };
    },
    setCartBillingAddress: (
      state,
      action: { payload: Partial<StateData['billAddress']> }
    ) => {
      return {
        ...state,

        billAddress: {
          ...state.billAddress,
          city: action.payload.city,
          addressLine1: action.payload.addressLine1,
          addressLine2: action.payload.addressLine2,
          state: action.payload.state,
          postalCode: action.payload.postalCode,
          country: action.payload.country,
        },
      };
    },
    setCartDataLoaded: (state, action: { payload: boolean }) => {
      return {
        ...state,
        controls: {
          ...state.controls,
          cartDataLoaded: action.payload,
        },
      };
    },
    setDisableAutoComplete: (state, action: { payload: boolean }) => {
      return {
        ...state,
        disableAutoComplete: action.payload,
      };
    },
    setGuestOrderLimitExceeded: (state, action: { payload: boolean }) => {
      return {
        ...state,
        guestOrderLimitExceeded: action.payload,
      };
    },
    setHasBadCoupon: (state, action: { payload: boolean }) => {
      return {
        ...state,
        controls: { ...state.controls, hasBadCoupon: action.payload },
      };
    },
  },

  extraReducers(builder) {},
});

export const {
  addUnsavedChanges,
  removeUnsavedChanges,
  updateItemQty,
  setLoading,
  updateBillingInfo,
  updateShippingAddress,
  updateAutoshipDetails,
  setSameAsShippingAddress,
  updateCardInfo,
  updateBillingAddress,
  setState,
  setIsPws,
  setTotals,
  setCityDropdown,
  addItem,
  setItems,
  setError,
  setEditingControls,
  setEditingAlert,
  setSnackbar,
  setCartPaymentInfo,
  setCartPaymentType,
  setCartShippingAddress,
  setCartBillingAddress,
  setDisableSubmitOrder,
  setCartSameAsShipping,
  setCartShipViaOptions,
  setCartShipVia,
  setCartWarningMessage,
  setCartTotals,
  setCartDiscountPercent,
  setCartDataLoaded,
  toggleCartAutoshipFrequency,
  setCartAutoshipEnabled,
  setHideErrorModal,
  setDisableAutoComplete,
  setCartCouponCode,
  setCartIsPvq,
  setCheckoutError,
  setGuestOrderLimitExceeded,
  setHasBadCoupon,
  setCartShipGeoCode,
} = autoshipSlice.actions;

export default autoshipSlice.reducer;
